import { format } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Button } from '@/components/button/button'
import { Typography } from '@/components/typography/typography'
import { cn } from '@/libs/tw'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { translate } from '@/utils/translator'

type MinNightsPopupProps = {
	date: Date
	earliestDeparture: Date
	minNights: number
}

function offset(el: HTMLElement) {
	const rect = el.getBoundingClientRect(),
		scrollLeft = window.scrollX || document.documentElement.scrollLeft,
		scrollTop = window.scrollY || document.documentElement.scrollTop
	return { top: rect.top + scrollTop, left: rect.left + scrollLeft, width: rect.width }
}

export const MinNightsPopup = ({ date, earliestDeparture, minNights }: MinNightsPopupProps) => {
	const setTravelPeriod = useCartStore.use.setTravelPeriod()
	const resetTravelPeriod = useCartStore.use.resetTravelPeriod()
	const popUpRef = useRef<HTMLDivElement>(null)
	const [position, setPosition] = useState<{ top: string; left: string }>()
	const [arrowPosition, setArrowPosition] = useState<{ top: number; left: number }>()

	const dayString = format(date, 'yyyy-MM-dd')

	useEffect(() => {
		setTimeout(() => {
			const element = document.querySelector(`[aria-label="${dayString}"]`) as HTMLElement
			if (!element) return

			const popUpHight = popUpRef.current?.getBoundingClientRect().height ?? 0
			const popUpWidth = popUpRef.current?.getBoundingClientRect().width ?? 0
			const dayPosition = offset(element)

			setArrowPosition({
				top: dayPosition.top - 25,
				left: dayPosition.left - 30 + dayPosition.width / 2,
			})

			const left = dayPosition.left - popUpWidth / 2 + dayPosition.width / 2

			if (left < 0) {
				setPosition({
					top: `${dayPosition.top - popUpHight - 20}px`,
					left: `${0}px`,
				})

				return
			}

			const right = left + popUpWidth
			if (right > document.body.clientWidth) {
				const diff = right - document.body.clientWidth

				setPosition({
					top: `${dayPosition.top - popUpHight - 20}px`,
					left: `${left - diff}px`,
				})
				return
			}

			setPosition({
				top: `${dayPosition.top - popUpHight - 20}px`,
				left: `${left}px`,
			})
		}, 50)
	}, [dayString])

	const handleSetPeriod = () => {
		setTravelPeriod(date, earliestDeparture)
	}

	const handleCancel = () => {
		resetTravelPeriod()
	}

	return createPortal(
		<>
			<div
				data-testid="min-night-popup"
				ref={popUpRef}
				className={cn(
					'absolute z-20 flex w-[342px] flex-col gap-24 rounded-8 bg-minNight-bg p-24 pt-16 text-minNight-text'
				)}
				style={{ top: position?.top, left: position?.left }}
			>
				<Typography element="p" style="p-bold">
					{translate('travel-period-min-stay-head')}
				</Typography>
				<Typography element="p" style="p">
					{translate('travel-period-min-stay-text', [
						minNights,
						format(earliestDeparture, 'dd.MM.yyyy'),
					])}
				</Typography>
				<div className={cn('flex flex-col gap-16')}>
					<Button
						data-testid="min-night-popup-adjust"
						style="primary"
						onClick={handleSetPeriod}
						className={cn('bg-minNight-text text-minNight-bg')}
					>
						{translate('travel-period-adjust-period')}
					</Button>
					<Button
						data-testid="min-night-popup-cancel"
						style="transparent"
						onClick={handleCancel}
						className={cn('minNight-text border text-minNight-text')}
					>
						{translate('travel-period-adjust-cancel')}
					</Button>
				</div>
			</div>
			<div
				className={cn(
					'absolute bottom-[-20px] h-0 w-0 border-l-[30px] border-r-[30px] border-t-[25px] border-minNight-bg border-l-transparent border-r-transparent bg-transparent'
				)}
				style={{ left: arrowPosition?.left, top: arrowPosition?.top }}
			/>
		</>,
		document.body
	)
}
